import PropTypes from 'prop-types';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import { useAuth } from 'contexts/auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getLocaleMap } from 'utils/locales';
import { useNavigate } from 'react-router-dom';
import { handleErrorResponse, formatRelativeTimeShort } from 'utils/general';
import { rawMessageIconColor } from 'utils/rawMessageIcon';
import { useDefaultListSize, listSizeOptions } from 'utils/listSize';
import { Autocomplete, Box, Dialog, DialogActions, DialogContent, IconButton, styled, Switch, Tab, Tabs, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment';
import DataTable from 'components/DataTable';
import { withLoading } from 'utils/hoc';
import isString from 'lodash/isString';
import get from 'lodash/get';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import filter from 'lodash/filter';
import some from 'lodash/some';
import cloneDeep from 'lodash/cloneDeep';
import sortedUniq from 'lodash/sortedUniq';
import escapeRegExp from 'lodash/escapeRegExp';
import getFp from 'lodash/fp/get';
import pickFp from 'lodash/fp/pick';
import CloseIcon from '@mui/icons-material/Close';
import Button from 'components/Button';
import Select from 'components/Select';
import { getData } from 'api/general';
import Image from 'components/Image';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { getBrands, getModels, getSources } from 'api/monitors';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import { getUserIWatchAccounts } from 'api/iwatch';
import { multiWordfilterOptions } from 'components/AutoComplete';
import { parsePhoneNumberFromString, ParseError } from 'libphonenumber-js/max';

const ProductListSection = ({ table_name, header_color, text_color, show_header, extra_params, refresh_second, page_options, sort_by_options, currency_options, source_options, no_image_placeholder_url, setLoading, sx, ...props }) => {
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const defaultListSize = useDefaultListSize(page_options);
  const [pageSize, setPageSize] = useState(defaultListSize);
  useEffect(() => {
    setPageSize(defaultListSize);
  }, [defaultListSize]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [rowsData, setRowsData] = useState([]);

  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshTimeInterval, setRefreshTimeInterval] = useState(null);

  const [tabValue, setTabValue] = useState('sell');
  const [product, setProduct] = useState();
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);

  const [brandFilter, setBrandFilter] = useState('all');
  const [modelFilter, setModelFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState();
  const [currencyFilter, setCurrencyFilter] = useState();
  const [filterParams, setFilterParams] = useState({});

  const [sortByFilter, setSortByFilter] = useState('createddate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

  const [brandOptions, setBrandOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [accountKeys, setAccountKeys] = useState({});
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [sortByOptions, setSortByOptions] = useState([]);

  const [keywordOptions, setKeywordOptions] = useState([]);
  const [allModelOptions, setAllModelOptions] = useState([]);
  const [filterModelOptions, setFilterModelOptions] = useState([]);
  const [models, setModels] = useState([]);

  // const sourceOptions = useMemo(() => source_options.map((option) => option), [source_options]);

  const [lm, setLm] = useState({});
  useEffect(() => {
    getLocaleMap([])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const CustomizedSwitch = styled((switchProps) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...switchProps} />
  ))(() => ({
    'width': 40,
    'height': 20,
    'padding': 0,
    '& .MuiSwitch-switchBase': {
      'padding': 0,
      'transitionDuration': '300ms',
      '&.Mui-checked': {
        'transform': 'translateX(20px)',
        'color': '#fff',
        'borderColor': 'white !important',
        '& + .MuiSwitch-track': {
          backgroundColor: '#61e048 !important',
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
        '& .MuiSwitch-thumb': {
          borderColor: '#61e048 !important',
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: '#33cf4d',
        border: '6px solid #fff',
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 1,
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 20,
      height: 20,
    },
    '& .MuiSwitch-track': {
      borderRadius: 20,
      height: 20,
      width: 40,
      backgroundColor: 'lightgray',
      opacity: 1,
    },
  }));

  const handleShowProductDetail = useCallback((data) => {
    setProduct(data);
    setIsProductDialogOpen(true);
  }, []);

  const handleClickContact = useCallback((e, data) => {
    e.stopPropagation();
    const cn = data.contact_number;
    if (cn) {
      const contactNumber = get(cn, 'phone', cn);
      const contactName = get(cn, 'author', data.contact_name);
      navigate(`/contact_detail?contact_name=${encodeURIComponent(contactName)}&contact_number=${encodeURIComponent(contactNumber)}`);
    }
  }, [navigate]);

  const columns = [
    {
      field: 'product_id',
      sortable: false,
      renderContent: (data) => {
        const { brand, model, description, condition, price, currency, contact_name, contact_number, createddate, model_id, message_id, year, month, account } = data;
        // eslint-disable-next-line no-return-assign, no-param-reassign
        const productTags = map(get(data, 'product_tags', []), 'tag');
        if (!some(productTags, (tag) => 'new' === tag || 'used' === tag)) {
          productTags.unshift(condition);
        }
        const modelIdName = get(model_id, 'id_name');
        const modelBrandName = get(model_id, 'brand_name');
        const contactNumber = get(contact_number, 'phone', contact_number);
        const contactFullName = get(contact_number, 'author', contact_name);
        const contactName = contactFullName?.split(/\s/).reduce((response, word) => response + word.slice(0, 1).toUpperCase(), '').slice(0, 2);
        const whatsappMessage = `${tabValue} ${modelIdName} ${currency} ${price?.toLocaleString()}`;
        const createddateMoment = moment(createddate);
        let messageDatetimeStr = null;
        const messageDatetime = data.datetime;
        if (messageDatetime != null) {
          const messageDatetimeMoment = moment(messageDatetime);
          const timeDiff = messageDatetimeMoment.diff(createddateMoment, 'minutes');
          if (Math.abs(timeDiff) >= 1) {
            messageDatetimeStr = formatRelativeTimeShort(messageDatetimeMoment);
          }
        }

        const imageUrl = data.image_url || model_id?.default_image?.file_url;
        const displayModelName = modelIdName?.replace(/_DEFAULT$/, '');
        const priceText = [currency, price != null ? price.toLocaleString() : null].filter(Boolean).join(' ');
        return (
          <MKBox
            width="100%"
            display="flex"
            alignItems="stretch"
            // Negative margin cancels the p={1} MKTable cell padding so the
            // card (and its divider) spans the full screen width.
            style={{ cursor: 'pointer', borderBottom: '3px solid #d8dde3', margin: '-8px', padding: '7px 8px 6px', gap: '8px' }}
            onClick={() => handleShowProductDetail(data)}
          >
            {imageUrl ? (
              <Image
                src={imageUrl}
                width="76px"
                height="86px"
                objectFit="cover"
                style={{ flexShrink: 0, borderRadius: '6px', display: 'block' }}
              />
            ) : (
              <MKBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="76px"
                height="86px"
                flexShrink={0}
                sx={{ borderRadius: '6px', backgroundColor: '#eef1f6' }}
              >
                {no_image_placeholder_url && (
                  <Image
                    src={no_image_placeholder_url}
                    width="56px"
                    objectFit="contain"
                    style={{ display: 'block', opacity: 0.45 }}
                  />
                )}
              </MKBox>
            )}
            <MKBox display="flex" flex={1} minWidth={0} flexDirection="column">
              <MKBox display="flex" alignItems="center" justifyContent="space-between" sx={{ gap: '6px' }}>
                <MKTypography fontSize="10px" fontWeight="bold" color="gray" sx={{ letterSpacing: '1px', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {modelBrandName}
                </MKTypography>
                <MKBox display="flex" alignItems="center" sx={{ gap: '5px', flexShrink: 0 }}>
                  {message_id && (
                    <LibraryBooksOutlinedIcon
                      style={{ cursor: 'pointer', fontSize: '1rem', flexShrink: 0, color: rawMessageIconColor(accountKeys[account]) }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`raw_message_details?id=${message_id}`, '_blank');
                      }}
                    />
                  )}
                  <MKTypography fontSize="10px" color="gray" textAlign="right" sx={{ lineHeight: 1.2 }}>
                    {formatRelativeTimeShort(createddateMoment)}
                    {messageDatetimeStr != null && (
                      <span style={{ color: 'red', marginLeft: '4px' }}>{messageDatetimeStr}</span>
                    )}
                  </MKTypography>
                </MKBox>
              </MKBox>
              <MKBox display="flex" sx={{ gap: '6px' }} mt={0.25}>
                <MKBox flex={1} minWidth={0}>
                  <MKTypography fontSize="14px" fontWeight="bold" color={text_color} sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.25 }}>
                    {displayModelName}
                  </MKTypography>
                  <MKTypography fontSize="11px" color={text_color} mt={0.25} sx={{ lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                    {description}
                  </MKTypography>
                  <MKBox display="flex" sx={{ gap: '5px', flexWrap: 'wrap' }} mt={0.5}>
                    {productTags.map((tag) => {
                      return <MKTypography fontSize="9px" color={text_color} padding="2px 5px" sx={{ backgroundColor: header_color, borderRadius: '3px', fontWeight: 'bold' }}>
                        {tag}
                      </MKTypography>
                    })}
                  </MKBox>
                </MKBox>
                <MKBox display="flex" flexDirection="column" alignItems="flex-end" flexShrink={0}>
                  <MKTypography fontSize="14px" fontWeight="bold" color={text_color} sx={{ whiteSpace: 'nowrap' }}>
                    {priceText}
                  </MKTypography>
                  <MKBox display="flex" alignItems="center" sx={{ gap: '8px' }} mt={1}>
                    <MKBox onClick={(e) => handleClickContact(e, data)} bgColor="#3259a1" color="white" display="flex" justifyContent="center" alignItems="center" sx={{ borderRadius: '50%', cursor: 'pointer' }} width="30px" height="30px" fontSize="13px" flexShrink={0}>
                      {contactName}
                    </MKBox>
                    <WhatsAppIcon
                      style={{ cursor: 'pointer' }}
                      sx={{ fontSize: '30px !important' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const whatsappNumber = contactNumber?.replace(/\D/g, '');
                        if (whatsappNumber) {
                          window.open(`//api.whatsapp.com/send?phone=${encodeURIComponent(whatsappNumber)}&text=${encodeURIComponent(whatsappMessage)}`, '_blank');
                        } else {
                          alert('Invalid phone number');
                        }
                      }}
                    />
                  </MKBox>
                </MKBox>
              </MKBox>
            </MKBox>
          </MKBox>
        );
      },
    },
  ];

  const fetchProductsFromApi = useCallback(() => {
    setLoading(true);
    const skip = currentPage * pageSize;
    const dataParams = {
      $expand: 'model_id/default_image,contact_number,product_tags',
      $orderBy: `${sortByFilter} ${sortDirection}`,
      $skip: skip,
      $top: pageSize,
      ...extra_params,
      // ...(keyword && { keyword }),
      ...filterParams,
    };

    if (keyword) {
      let matchedModelIds;
      if (isString(keyword)) {
        const keywordPattern = new RegExp(escapeRegExp(keyword), 'i');
        matchedModelIds = filter(models, (model) => {
          const idName = model.id_name;
          return idName && keywordPattern.test(idName);
        }).map(getFp('model_id'));
      } else {
        matchedModelIds = [keyword.model_id];
      }

      let newModelIdAdded = false;

      const existingModelId = dataParams.model_id;
      if (existingModelId) {
        matchedModelIds.push(existingModelId);
        newModelIdAdded = true;
      }

      const existingModelIds = dataParams['model_id[in]'];
      if (existingModelIds) {
        const existingModelIdsArray = existingModelIds.split(',');
        if (existingModelIdsArray.length > 0) {
          Array.prototype.push.apply(matchedModelIds, existingModelIdsArray);
          newModelIdAdded = true;
        }
      }

      if (newModelIdAdded) {
        matchedModelIds.sort();
        matchedModelIds = sortedUniq(matchedModelIds);
      }

      if (matchedModelIds && matchedModelIds.length > 0) {
        dataParams['model_id[in]'] = matchedModelIds.join();
        delete dataParams.model_id;
      }
      if (dataParams['model_id[in]']) {
        delete dataParams['brand_id'];
      }
    }

    if (!dataParams.account && !dataParams['account[in]']) {
      const user_iwatch_accounts = auth?.user?.user_iwatch_accounts;
      if (user_iwatch_accounts && user_iwatch_accounts.length > 0) {
        const accountNames = user_iwatch_accounts.map(userIWatchAccount => userIWatchAccount?.iwatch_account?.name);
        dataParams['account[in]'] = accountNames.join();
      } else {
        dataParams['product_id[null]'] = true;
      }
    }

    if (['sell', 'buy'].includes(tabValue)) {
      dataParams.monitor_type = tabValue;
    } else {
      dataParams.condition = tabValue;
      dataParams.monitor_type = 'sell';
    }

    return getData(`/${table_name}`, dataParams, 'bearer')
      .then(({ data, headers }) => {
        // const productIds = map(data, 'product_id');
        const contentRange = get(headers, 'content-range');
        const size = Number(contentRange.split('/')[1]);
        // setRowsData(data);
        // setTotalRows(size);
        let messagePromise = null;
        if (table_name === 'products' && skip === 0 && size > 0 && data.length > 0) {
          const messageId = data[0].message_id;
          if (messageId != null) {
            const firstMessageDataParams = {
              id: messageId,
              $select: 'datetime'
            };
            messagePromise = getData('/whatsapp_messages', firstMessageDataParams, 'bearer').then(messageRes => {
              if (messageRes.data.length > 0) {
                const datetime = messageRes.data[0].datetime;
                data[0].datetime = datetime;
              }
            });
          }
        }
        return Promise.resolve(messagePromise).then(() => {
          setRowsData(data);
          setTotalRows(size);
        });
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setLoading, sortByFilter, sortDirection, currentPage, pageSize, extra_params, keyword, filterParams, tabValue, table_name, setAuth]);

  const listRef = useRef(null);
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [currentPage, pageSize, sortByFilter, sortDirection, keyword, filterParams, tabValue]);

  useEffect(() => {
    if (table_name) {
      fetchProductsFromApi();
    }
  }, [fetchProductsFromApi, table_name]);

  useEffect(() => {
    if (autoRefresh && refreshTimeInterval == null) {
      const refreshDataInterval = setInterval(() => {
        fetchProductsFromApi();
      }, refresh_second * 1000);
      setRefreshTimeInterval(refreshDataInterval);
    }
  }, [autoRefresh, refreshTimeInterval, refresh_second, fetchProductsFromApi]);

  useEffect(() => {
    if (!autoRefresh && refreshTimeInterval != null) {
      clearInterval(refreshTimeInterval);
      setRefreshTimeInterval(null);
    }
  }, [autoRefresh, refreshTimeInterval]);

  useEffect(() => {
    return () => {
      clearInterval(refreshTimeInterval);
    };
  }, [refreshTimeInterval]);

  const handleChangeTab = useCallback((event, newTabValue) => {
    setTabValue(newTabValue);
    if (autoRefresh && refreshTimeInterval != null) {
      setAutoRefresh(false);
    }
  }, [autoRefresh, refreshTimeInterval]);

  const handleConfirmFilter = useCallback(() => {
    setIsFilterDialogOpen(false);

    let matchedModelIds = null;
    if (modelFilter) {
      if (isString(modelFilter)) {
        matchedModelIds = filter(models, (model) => {
          const idName = model.id_name;
          return idName && idName.includes(modelFilter);
        }).map(getFp('model_id'));
      } else {
        matchedModelIds = [modelFilter.model_id];
      }
    }

    const newFilteredParams = {};
    if (matchedModelIds && matchedModelIds.length > 0) {
      newFilteredParams['model_id[in]'] = matchedModelIds.join();
    }
    if (brandFilter && brandFilter !== 'all' && !newFilteredParams['model_id[in]']) {
      // only set brand filter if model is not set since these 2 values may not in sync
      // in some prodcuts
      const brand = brandOptions.find(brandOption => brandOption.value === brandFilter);
      const brandName = brand && brand.label;
      if (brandName && brandName.length > 0) {
        // newFilteredParams.brand = brandFilter;
        newFilteredParams['brand_id'] = brandFilter;
      }
    }
    if (currencyFilter && currencyFilter.length > 0) {
      newFilteredParams['currency[in]'] = currencyFilter.join();
    }
    if (sourceFilter && sourceFilter.length > 0) {
      newFilteredParams['account[in]'] = sourceFilter.join();
    }
    setFilterParams(newFilteredParams);
    setCurrentPage(0);
  }, [brandFilter, currencyFilter, modelFilter, sourceFilter]);

  const resetFilter = useCallback(() => {
    setFilterParams({});
    setBrandFilter('all');
    setModelFilter('');
    setCurrencyFilter(null);
    setSourceFilter(null);
    setSortByFilter('createddate');
    setSortDirection('desc');
    setCurrentPage(0);
  }, []);

  const handleResetFilter = useCallback(() => {
    resetFilter();
    setIsFilterDialogOpen(false);
  }, []);

  const handleClearAll = useCallback(() => {
    resetFilter();
    setKeyword('');
  }, []);

  const fetchBrandsFromApi = useCallback(() => {
    return getBrands()
      .then(({ data }) => {
        const options = data.map((datum) => ({
          // value: datum.name,
          value: datum.brand_id,
          label: datum.name,
        }));
        setBrandOptions([{ value: 'all', label: 'All' }, ...options]);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [setAuth]);

  const fetchModelsFromApi = useCallback(() => {
    return getModels({ $orderby: 'id_name' })
      .then(({ data }) => {
        const pickFn = pickFp(['model_id', 'id_code', 'id_name', 'brand_name', 'brand']);
        const newAllModelOptions = map(data, pickFn);
        const newKeywordOptions = cloneDeep(newAllModelOptions);
        setModels(data);
        setAllModelOptions(newAllModelOptions);
        setKeywordOptions(newKeywordOptions);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [setAuth]);

  const fetchSourcesFromApi = useCallback(() => {
    return getSources()
      .then(({ data }) => {
        setSourceOptions(data.map((datum) => {
          return {
            value: datum.name,
            label: datum.name,
          };
        }));
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        // setLoading(false);
      });
  }, [setAuth]);

  const fetchUserIWatchAccountsFromApi = useCallback(() => {
    if (!auth?.user) {
      return Promise.resolve();
    }
    return getUserIWatchAccounts({ $expand: 'iwatch_account', user: auth?.user?.username })
      .then(({ data }) => {
        const iWatchAccounts = sortBy(map(data, 'iwatch_account'), ['sequence']);
        setAccountKeys(iWatchAccounts.reduce((keys, iWatchAccount) => {
          return { ...keys, [iWatchAccount.name]: iWatchAccount.key };
        }, {}));
        const newSourceAccountOptions = map(iWatchAccounts, (iWatchAccount) => {
          const accountName = iWatchAccount.name;
          const phone = iWatchAccount.phone;
          try {
            const phoneNumber = parsePhoneNumberFromString('+' + phone);
            const formattedPhoneNumber = phoneNumber && phoneNumber.formatInternational() || phone;
            return {
              label: `${iWatchAccount.key} (${accountName}) (${formattedPhoneNumber})`,
              value: accountName,
            };
          } catch (e) {
            if (e instanceof ParseError) {
              // Not a phone number, non-existent country, etc.
              console.log(e.message);
            } else {
              throw e;
            }
          }
        });

        // newSourceAccountOptions.unshift({ label: '(none)', value: null });
        setSourceOptions(newSourceAccountOptions);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [auth, setAuth]);

  useEffect(() => {
    fetchBrandsFromApi();
    fetchModelsFromApi();
    fetchUserIWatchAccountsFromApi();
  }, [fetchBrandsFromApi, fetchModelsFromApi, fetchUserIWatchAccountsFromApi]);

  useEffect(() => {
    if (currency_options) {
      setCurrencyOptions(currency_options);
    }
  }, [currency_options]);

  useEffect(() => {
    if (sort_by_options) {
      setSortByOptions(sort_by_options);
    }
  }, [sort_by_options]);

  // useEffect(() => {
  //   if (source_options) {
  //     setSourceOptions(source_options);
  //   }
  // }, [source_options]);

  const handleChangeSortDirection = useCallback(() => {
    setSortDirection((prevDirection) => (prevDirection === 'asc' ? 'desc' : 'asc'));
  }, []);

  const handleKeywordChange = (event, newValue) => {
    setKeyword(newValue);
    setCurrentPage(0);
  };

  // Android soft keyboards only send Enter when the action key is a search/go
  // key, which enterKeyHint asks for; this also searches the typed text when no
  // suggestion is highlighted.
  const handleKeywordKeyDown = (event) => {
    if (event.key === 'Enter' && !event.defaultPrevented) {
      handleKeywordChange(event, event.target.value);
    }
  };

  useEffect(() => {
    let newFilterModelOptions;
    if (brandFilter && brandFilter !== 'all') {
      newFilterModelOptions = filter(allModelOptions, ['brand', brandFilter]);
    } else {
      newFilterModelOptions = allModelOptions;
    }
    setFilterModelOptions(newFilterModelOptions);
  }, [brandFilter, allModelOptions]);

  return (
    <MKBox
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
      flex={1}
      minHeight={0}
      overflow="hidden"
      {...props}
      sx={{
        // On a locked page the wrapper chain above is a flex column that
        // fills the scroll box, so flex={1} gives this list a definite height
        // and only its rows scroll.
        ...sx,
      }}
    >
      {/* Search bar, tabs and the refresh row are in flow above the list, and
          only the list scrolls, so they stay on screen without fixed or
          sticky positioning. */}
      <MKBox
        display="flex"
        width="100%"
        flexDirection="column"
        alignItems="center"
        bgColor={header_color}
        padding="0.5rem 1rem"
        flexShrink={0}
      >
        <MKBox
          display="flex"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
        >
          <MKBox display="flex" bgColor="white" width="100%" alignItems="center" style={{ borderRadius: 10, border: '1px solid #d8dbe0' }}>
            <SearchIcon fontSize="medium" style={{ marginLeft: '0.875rem', marginRight: '0.625rem' }} />
            <MKBox flex={1} minWidth={0}>
              <Autocomplete
                freeSolo
                disableClearable
                options={keywordOptions}
                value={keyword}
                fullWidth
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  return `${option.id_name} (${option.brand_name}) (${option.id_code})`;
                }}
                onChange={handleKeywordChange}
                renderInput={(params) => {
                  // eslint-disable-next-line no-param-reassign
                  params.inputProps.style = { fontSize: '18px' };
                  // eslint-disable-next-line no-param-reassign
                  params.inputProps.enterKeyHint = 'search';
                  // eslint-disable-next-line no-param-reassign
                  params.inputProps.inputMode = 'search';
                  return (
                    <TextField
                      placeholder="Search by brand / model / code"
                      {...params}
                      onKeyDown={handleKeywordKeyDown}
                    />
                  );
                }}
                filterOptions={multiWordfilterOptions}
              />
            </MKBox>
            <IconButton
              size="small"
              onClick={handleClearAll}
              sx={{ marginRight: '6px', width: 30, height: 30, flexShrink: 0, backgroundColor: '#eceff3', '&:hover': { backgroundColor: '#e2e6ea' } }}
            >
              <CloseIcon sx={{ fontSize: 15, color: '#777' }} />
            </IconButton>
          </MKBox>
          {/* <Paper
            component="form"
            sx={{ p: 0.5, display: 'flex', alignItems: 'center', width: '100%' }}
          > */}
            {/* <InputBase
              sx={{ ml: 2, flex: 1 }}
              placeholder="Search by brand / model / code"
              onChange={(e) => {
                setKeyword(e.target.value);
                setAutoRefresh(false);
              }}
            /> */}
          {/* </Paper> */}
        </MKBox>
      </MKBox>
      <MKBox display="flex" width="100%" alignItems="center" flexShrink={0} style={{ margin: '6px 0' }}>
        <Tabs
          value={tabValue}
          onChange={handleChangeTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          style={{ backgroundColor: 'transparent', width: '100%' }}
          TabIndicatorProps={{
            style: {
              backgroundColor: '#eacb38',
              zIndex: 5,
            },
          }}
          sx={{
            '.MuiButtonBase-root ': {
              color: `${text_color} !important`,
            },
            '.MuiTab-root': {
              minWidth: 0,
              padding: '9px 10px',
            },
            '.MuiTabs-flexContainer': {
              justifyContent: 'space-between',
            },
          }}
        >
          <Tab value="sell" label="Sell" />
          <Tab value="new" label="Sell-New" />
          <Tab value="used" label="Sell-Used" />
          <Tab value="buy" label="Wanted" />
        </Tabs>
      </MKBox>
      {/* Refresh / Auto Refresh / filter grouped into one dedicated row below
          the tabs: refresh pinned to the far left, Auto Refresh + filter
          grouped on the far right. A fixed (shorter) row height keeps the
          bigger refresh icon from growing the row. */}
      <MKBox
        display="flex"
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        bgColor="#eaf2fb"
        px={1.5}
        flexShrink={0}
        style={{ minHeight: '34px' }}
      >
        <RefreshIcon
          style={{ cursor: 'pointer', color: text_color, fontSize: '30px' }}
          onClick={() => fetchProductsFromApi()}
        />
        <MKBox display="flex" alignItems="center" style={{ gap: '0.625rem' }}>
          <MKTypography color={text_color} fontSize="13px">Auto Refresh</MKTypography>
          <CustomizedSwitch
            checked={autoRefresh}
            onChange={() => setAutoRefresh(!autoRefresh)}
          />
          <Box sx={{ width: '1px', height: '22px', bgcolor: '#ddd' }} />
          <MKBox display="flex" alignItems="center" style={{ cursor: 'pointer' }}>
            <FilterAltOutlinedIcon fontSize="medium" onClick={() => setIsFilterDialogOpen(true)} style={{ color: text_color }} />
            {sortDirection === 'asc'
              ? <KeyboardArrowUpIcon fontSize="small" onClick={handleChangeSortDirection} style={{ color: text_color }} />
              : <KeyboardArrowDownIcon fontSize="small" onClick={handleChangeSortDirection} style={{ color: text_color }} />}
          </MKBox>
        </MKBox>
      </MKBox>
      <MKBox
        ref={listRef}
        display="flex"
        flexDirection="column"
        width="100%"
        flex={1}
        minHeight={0}
        sx={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
      >
        <DataTable
          data={rowsData}
          columns={columns}
          showHeader={show_header}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          rowsPerPage={pageSize}
          rowsPerPageOptions={listSizeOptions(page_options, pageSize)}
          totalCount={/*totalRows*/ -1}
          onRowsPerPageChange={setPageSize}
        />
      </MKBox>
      <Dialog
        open={isFilterDialogOpen}
        fullWidth
      >
        <MKBox display="flex" width="100%" justifyContent="flex-end" padding={1}>
          <CloseIcon fontSize="medium" onClick={() => setIsFilterDialogOpen(false)} style={{ cursor: 'pointer' }} />
        </MKBox>
        <DialogContent>
          <MKTypography fontSize="16px" style={{ marginBottom: '0.25rem', marginTop: '1rem' }}>Source Feeder Account(s)</MKTypography>
          <Select
            value={sourceFilter}
            onChange={(v) => setSourceFilter(v)}
            options={sourceOptions}
            fullWidth
            multiple
            style={{ marginBottom: '1rem' }}
          />
          <MKTypography fontSize="16px" style={{ marginBottom: '0.25rem' }}>Brand</MKTypography>
          <Select
            value={brandFilter}
            onChange={(v) => {
              setBrandFilter(v);
              setModelFilter('');
            }}
            options={brandOptions}
            fullWidth
            style={{ marginBottom: '1rem' }}
          />
          <MKTypography fontSize="16px" style={{ marginBottom: '0.25rem' }}>Product</MKTypography>
          {/* <Select
            value={modelFilter}
            onChange={(v) => setModelFilter(v)}
            options={modelOptions}
            fullWidth
            style={{ marginBottom: '1rem' }}
          /> */}
          <Autocomplete
            freeSolo
            options={filterModelOptions}
            value={modelFilter}
            onChange={(event, newValue) => {
              setModelFilter(newValue);
            }}
            fullWidth
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option;
              }
              return `${option.id_name} (${option.id_code})`;
            }}
            renderInput={(params) => {
              // eslint-disable-next-line no-param-reassign
              params.inputProps.style = { fontSize: '14px', color: '#495057' };
              return (
                <TextField
                  {...params}
                />
              );
            }}
            filterOptions={multiWordfilterOptions}
          />
          <MKTypography fontSize="16px" style={{ marginBottom: '0.25rem' }}>Currency</MKTypography>
          <Select
            value={currencyFilter}
            onChange={(v) => setCurrencyFilter(v)}
            options={currencyOptions}
            fullWidth
            style={{ marginBottom: '1rem' }}
            multiple
          />
          <MKTypography fontSize="16px">Sort By</MKTypography>
          <Select
            value={sortByFilter}
            onChange={(v) => setSortByFilter(v)}
            options={sortByOptions}
            fullWidth
            style={{ marginBottom: '1rem' }}
          />
        </DialogContent>
        <DialogActions>
          <MKBox display="flex" width="100%" justifyContent="space-around">
            <Button fullWidth style={{ marginRight: '1rem', backgroundColor: 'white', borderColor: '1px solid black', color: 'black' }} onClick={handleResetFilter}>
              Reset
            </Button>
            <Button fullWidth style={{ backgroundColor: '#eacb37', color: 'black' }} onClick={handleConfirmFilter}>
              Confirm
            </Button>
          </MKBox>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isProductDialogOpen}
        fullWidth
      >
        {/* <MKBox display="flex" width="100%" justifyContent="flex-end" padding={1}>
          <CloseIcon fontSize="medium" onClick={() => setIsProductDialogOpen(false)} />
        </MKBox> */}
        <DialogContent style={{ display: 'flex', flexDirection: 'column' }}>
          <Image src={product?.image_url || product?.model_id?.default_image?.file_url} style={{ maxHeight: '50vh' }} />
          <MKTypography color={text_color} fontWeight="bold" textAlign="center" fontSize="16px">
            {product?.model_id?.id_name}
          </MKTypography>
          <MKTypography color={"gray"} textAlign="center" fontSize="16px">
            {product?.model_id?.brand_name}
          </MKTypography>
          <MKTypography color={text_color} textAlign="center" fontSize="16px">
            {product?.description}
          </MKTypography>
          <MKTypography color={text_color} textAlign="center" fontSize="16px">
            {`${product?.currency} ${product?.price?.toLocaleString()}`}
          </MKTypography>
          <Button fullWidth style={{ color: 'white', marginTop: '1rem' }} onClick={() => setIsProductDialogOpen(false)}>
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </MKBox>
  );
};

ProductListSection.propTypes = {
  table_name: PropTypes.string,
  header_color: PropTypes.string,
  text_color: PropTypes.string,
  page_options: PropTypes.string,
  setLoading: PropTypes.func,
  show_header: PropTypes.bool,
  extra_params: PropTypes.object,
  refresh_second: PropTypes.number,
  sort_by_options: PropTypes.array,
  currency_options: PropTypes.array,
  source_options: PropTypes.array,
  no_image_placeholder_url: PropTypes.string,
  sx: PropTypes.object,
};

ProductListSection.defaultProps = {
  table_name: '',
  header_color: '#2c589d',
  text_color: '#000',
  page_options: [],
  setLoading: null,
  show_header: false,
  extra_params: {},
  refresh_second: 10,
  sort_by_options: [],
  currency_options: [],
  source_options: [],
  no_image_placeholder_url: 'https://watchone.joyaethercloud.com/watch-server/contents/files/watch_one_mobile_logo.png',
};

export default withLoading(ProductListSection);
